/* eslint-disable @typescript-eslint/no-explicit-any */
// components/PaymentPage.tsx - Mobile Friendly & Responsive
import React, { useEffect } from "react";
import PaymentForm from "./PaymentForm";
import { usePaymentContext } from "../context/PaymentContext";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../context/DarkModeContext";

const PaymentPage: React.FC = () => {
  const { state, clearPayment } = usePaymentContext();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();

  // Clear payment state when component unmounts
  useEffect(() => {
    return () => {
      // Only clear if payment wasn't successful
      if (state.paymentIntent?.status !== "succeeded") {
        clearPayment();
      }
    };
  }, [clearPayment, state.paymentIntent]);

  const handleSuccess = (paymentIntent: any) => {
    console.log("Payment successful:", paymentIntent);
    navigate("/dashboard", {
      state: { message: "Payment completed successfully!" },
    });
  };

  const handleCancel = () => {
    clearPayment();
    navigate("/packages");
  };

  // Check if a plan is selected
  if (!state.selectedPlan) {
    return (
      <div
        className={`w-full min-h-screen flex items-center justify-center p-4 ${
          isDark ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <div
          className={`w-full max-w-md rounded-xl p-4 sm:p-6 lg:p-8 ${
            isDark
              ? "bg-yellow-900/20 border border-yellow-800"
              : "bg-yellow-50 border border-yellow-200"
          }`}
        >
          <div className="text-center">
            {/* Icon for better visual appeal */}
            <div
              className={`mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-3 sm:mb-4 ${
                isDark ? "bg-yellow-900/30" : "bg-yellow-100"
              }`}
            >
              <svg
                className={`w-6 h-6 sm:w-8 sm:h-8 ${
                  isDark ? "text-yellow-400" : "text-yellow-600"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            <h2
              className={`text-lg sm:text-xl lg:text-2xl font-semibold mb-2 sm:mb-3 ${
                isDark ? "text-yellow-300" : "text-yellow-800"
              }`}
            >
              No Plan Selected
            </h2>
            <p
              className={`text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed ${
                isDark ? "text-yellow-200" : "text-yellow-700"
              }`}
            >
              Please select a subscription plan before proceeding to payment.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => navigate(-1)}
                className={`flex-1 px-4 py-2 sm:py-3 border rounded-lg transition-colors font-medium text-sm sm:text-base ${
                  isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Go Back
              </button>
              <button
                onClick={() => navigate("/packages")}
                className="flex-1 px-4 py-2 sm:py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm sm:text-base"
              >
                Choose a Plan
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen py-4 sm:py-6 lg:py-8 ${
        isDark ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      <div className="container mx-auto px-3 sm:px-4 lg:px-6">
        {/* Header Section */}
        <div className="text-center mb-6 sm:mb-8 lg:mb-12">
          {/* Back Navigation for Mobile */}
          <div className="sm:hidden mb-4">
            <button
              onClick={handleCancel}
              className={`flex items-center gap-2 transition-colors text-sm ${
                isDark
                  ? "text-gray-400 hover:text-gray-300"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Plans
            </button>
          </div>

          {/* Main Title */}
          <h1
            className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Complete Your Payment
          </h1>

          {/* Subtitle */}
          <p
            className={`text-sm sm:text-base lg:text-lg max-w-md mx-auto ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Secure payment processing for your {state.selectedPlan.name} plan
          </p>
        </div>

        {/* Progress Indicator for larger screens */}
        <div className="hidden sm:flex justify-center mb-8 lg:mb-12">
          <div
            className={`flex items-center gap-4 text-sm ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">
                1
              </div>
              <span
                className={`font-medium ${
                  isDark ? "text-blue-400" : "text-blue-600"
                }`}
              >
                Select Plan
              </span>
            </div>
            <div className="w-8 h-0.5 bg-blue-500"></div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">
                2
              </div>
              <span
                className={`font-medium ${
                  isDark ? "text-blue-400" : "text-blue-600"
                }`}
              >
                Payment
              </span>
            </div>
          </div>
        </div>

        {/* Payment Form Container */}
        <div className="flex justify-center">
          <div className="w-full max-w-lg lg:max-w-2xl">
            {/* Plan Summary Card - Mobile Collapsible */}
            <div className="mb-4 sm:mb-6 lg:mb-8">
              <details
                className={`rounded-xl shadow-sm border overflow-hidden sm:details-open ${
                  isDark
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <summary className="flex items-center justify-between p-4 sm:p-6 cursor-pointer list-none">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isDark ? "bg-blue-900/30" : "bg-blue-100"
                      }`}
                    >
                      <svg
                        className={`w-5 h-5 sm:w-6 sm:h-6 ${
                          isDark ? "text-blue-400" : "text-blue-600"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3
                        className={`font-semibold text-sm sm:text-base ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {state.selectedPlan.name} Plan
                      </h3>
                      <p
                        className={`font-bold text-lg sm:text-xl ${
                          isDark ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        KES{" "}
                        {state.billingPeriod === "monthly"
                          ? state.selectedPlan.monthlyPrice
                          : state.selectedPlan.annualPrice}
                        <span
                          className={`font-normal text-sm sm:text-base ml-1 ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          /
                          {state.billingPeriod === "monthly" ? "month" : "year"}
                        </span>
                      </p>
                    </div>
                  </div>
                  <svg
                    className={`w-4 h-4 transform transition-transform sm:hidden ${
                      isDark ? "text-gray-500" : "text-gray-400"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>

                {/* Plan Details - Hidden on mobile by default */}
                <div
                  className={`px-4 sm:px-6 pb-4 sm:pb-6 border-t pt-4 ${
                    isDark ? "border-gray-700" : "border-gray-100"
                  }`}
                >
                  <h4
                    className={`font-medium mb-2 text-sm sm:text-base ${
                      isDark ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    Plan Features:
                  </h4>
                  <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                    {state.selectedPlan.features?.map(
                      (feature: string, index: number) => (
                        <li key={index} className="flex items-start gap-2">
                          <svg
                            className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span
                            className={
                              isDark ? "text-gray-300" : "text-gray-600"
                            }
                          >
                            {feature}
                          </span>
                        </li>
                      ),
                    )}
                  </ul>

                  {/* Billing Note */}
                  <div
                    className={`mt-3 p-2 sm:p-3 rounded-lg ${
                      isDark ? "bg-blue-900/20" : "bg-blue-50"
                    }`}
                  >
                    <p
                      className={`text-xs sm:text-sm ${
                        isDark ? "text-blue-300" : "text-blue-700"
                      }`}
                    >
                      <strong>Billing:</strong>{" "}
                      {state.billingPeriod === "monthly" ? "Monthly" : "Annual"}{" "}
                      subscription
                      {state.billingPeriod === "annual" && (
                        <span
                          className={`block mt-1 font-medium ${
                            isDark ? "text-green-400" : "text-green-600"
                          }`}
                        >
                          🎉 You save{" "}
                          {Math.round(
                            (1 -
                              state.selectedPlan.annualPrice /
                                (state.selectedPlan.monthlyPrice * 12)) *
                              100,
                          )}
                          % with annual billing!
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </details>
            </div>

            {/* Payment Form */}
            <div
              className={`rounded-xl shadow-sm border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <PaymentForm onSuccess={handleSuccess} onCancel={handleCancel} />
            </div>

            {/* Security Assurance Footer */}
            <div className="mt-4 sm:mt-6 text-center">
              <div
                className={`flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <div className="flex items-center gap-1 sm:gap-2">
                  <svg
                    className="w-4 h-4 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <span>256-bit SSL Encryption</span>
                </div>
                <div className="hidden sm:flex items-center gap-1 sm:gap-2">
                  <svg
                    className="w-4 h-4 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <span>PCI DSS Compliant</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
